import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  List,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  IconBorderAll,
  IconBorderNone,
  IconCalendar,
  IconCalendarOff,
  IconMail,
  IconMaximize,
  IconMinimize,
} from "@tabler/icons";
import { GetSessions, GetChatMessages } from "./actions";
import { useDispatch } from "react-redux";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

export default function UserAiChatSessions() {
  const params = useParams();
  const userId = params.userId;
  const theme = useTheme();
  let dataPrivileges = getLocalStorage(dataLocalStorage.privileges);
  const dispatch = useDispatch();
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatThreads, setChatThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingChats, setLoadingChats] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [offset, setOffset] = useState(0);
  const dataUser = getLocalStorage(dataLocalStorage.userinfo);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState({});
  const [v, setv] = useState("outlined");
  const [d, setd] = useState(false);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getMessages = (chatId, offsetVal) => {
    setLoadingMessages(true);
    dispatch(
      GetChatMessages({
        offset: offsetVal,
        session_id: chatId,
        callback: (data) => {
          setLoadingMessages(false);
          if (data.data) {
            setMessages((prev) => {
              // Avoid duplication
              const newIds = new Set(prev.map((msg) => msg.id));
              const filtered = data.data.filter((msg) => !newIds.has(msg.id));
              return [...prev, ...filtered];
            });
            setOffset((prev) => prev + 1);
          }
        },
      })
    );
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      if (scrollTop === 0 && !loadingMessages && selectedChatId) {
        getMessages(selectedChatId, offset);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectedChatId, offset, loadingMessages]);

  useEffect(() => {
    console.log("Selected chat changed:", selectedChatId);
    console.log("Offset reset:", offset);
    console.log("Messages cleared:", messages);
  }, [selectedChatId]);

  // API -> GET CHATS
  useEffect(() => {
    setLoadingChats(true);
    dispatch(
      GetSessions({
        userId: userId,
        callback: (data) => {
          setLoadingChats(false);
          console.log(data);
          if (data.data) {
            setChatThreads(data?.data);
          }
        },
      })
    );
  }, []);

  // API -> GET MESSAGES
  useEffect(() => {
    if (selectedChatId) {
      setMessages([]);
      setOffset(0);
      setLoadingMessages(true);
      dispatch(
        GetChatMessages({
          session_id: selectedChatId,
          offset: 0,
          callback: (data) => {
            setLoadingMessages(false);
            if (data.data) {
              setMessages(data.data);
              setOffset(10);
            }
          },
        })
      );
    }
  }, [selectedChatId]);

  return (
    <Box
      sx={{
        display: "flex",
        height: isExpanded ? "75vh" : "70vh",
        width: isExpanded ? "100%" : "100%",
        maxWidth: isExpanded ? "none" : "1200px",
        mx: "auto",
        transition: "all 0.3s ease-in-out",
      }}
    >
      {/* Sidebar */}
      <Paper
        variant={v}
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          mt: 4,
          p: 0,
          borderRadius: 2,
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            width: "250px",
            borderRight: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 0,
          }}
        >
          <Box borderRadius={0} p={2} borderBottom={1} borderColor="divider">
            <Typography variant="h6">
              Sessions ({chatThreads?.length ?? 0})
            </Typography>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              borderRadius: 0,
              scrollbarColor: "#c3cfe2 #e5ebf3", //thumb color, then track color
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e5ebf3",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c3cfe2",
                borderRadius: "4px",
                transition: "background-color 0.2s ease-in-out",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#c3cfe2", // Darker for clearer hover visibility
              },
            }}
          >
            <List dense>
              {loadingChats === true && chatThreads.length === 0 ? (
                <Box
                  display={"flex"}
                  width={"100%"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  mt={100}
                >
                  <CircularProgress size={20} />
                </Box>
              ) : (
                chatThreads &&
                chatThreads.length > 0 &&
                chatThreads?.map((chat, i) => {
                  const lastMessage = chat?.last_message;

                  return (
                    <ListItemButton
                      key={i}
                      selected={selectedChatId === chat.id}
                      onClick={() => {
                        setLoadingMessages(true);
                        setMessages([]); // clear messages for new chat
                        setOffset(0); // reset offset
                        dispatch(
                          GetChatMessages({
                            session_id: chat.id,
                            offset: 0,
                            callback: (data) => {
                              setLoadingMessages(false);
                              if (data.data) {
                                setSelectedChatId(chat.id);
                                setMessages(data.data);
                                setOffset(1); // initial offset after first load
                                setTimeout(() => {
                                  scrollToBottom();
                                }, 500);
                              }
                            },
                          })
                        );
                      }}
                      sx={{
                        alignItems: "flex-start",
                        borderBottom: "0.5px solid",
                        borderColor: "divider",
                        borderRadius: 0,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            textTransform={"capitalize"}
                            sx={{ maxWidth: "180px", fontWeight: "bold" }}
                          >
                            {chat?.title}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: "180px" }}
                          >
                            {moment(chat?.updatedAt).format(
                              "dddd, MMMM Do YYYY"
                            )}
                          </Typography>
                        }
                      />
                      {chat?.hasNewMessage && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            bgcolor: "red",
                            borderRadius: "50%",
                            ml: 1,
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Box>
        </Box>

        {/* Chat Area */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            borderRadius: 0,
          }}
        >
          {/* Chat Header */}
          <Box
            p={1}
            borderBottom={1}
            borderColor="divider"
            bgcolor="primary.main"
            color="white"
            sx={{
              borderTopRightRadius: 10,
              borderTopLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Tooltip
                placement="top"
                title={isExpanded ? "Minimize" : "Maximize"}
              >
                <IconButton
                  onClick={() => setIsExpanded((prev) => !prev)}
                  color="inherit"
                >
                  {isExpanded ? (
                    <IconMinimize size={20} />
                  ) : (
                    <IconMaximize size={20} />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip
                placement="top"
                title={v === "outlined" ? "Shadow" : "Bordered"}
              >
                <IconButton
                  onClick={() => {
                    if (v === "outlined") {
                      setv("elevation");
                    } else {
                      setv("outlined");
                    }
                  }}
                  color="inherit"
                >
                  {v === "outlined" ? (
                    <IconBorderAll size={20} />
                  ) : (
                    <IconBorderNone size={20} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip
                placement="top"
                title={d === false ? "Show dates" : "Hide dates"}
              >
                <IconButton
                  onClick={() => {
                    if (d === true) {
                      setd(false);
                    } else {
                      setd(true);
                    }
                  }}
                  color="inherit"
                >
                  {d === false ? (
                    <IconCalendar size={20} />
                  ) : (
                    <IconCalendarOff size={20} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Messages */}
          <Box
            ref={containerRef}
            flexGrow={1}
            overflow="auto"
            p={2}
            display="flex"
            flexDirection="column"
            borderRadius={0}
            gap={2}
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(to bottom, #1a1a1a, #2c3e50)" // Dark mode gradient
                  : "linear-gradient(to bottom, rgb(255, 255, 255), #c3cfe2)", // Light mode gradient
              scrollbarColor: "#c3cfe2 #e5ebf3", //thumb color, then track color
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e5ebf3",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c3cfe2",
                borderRadius: "4px",
                transition: "background-color 0.2s ease-in-out",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#c3cfe2", // Darker for clearer hover visibility
              },
            }}
          >
            {loadingMessages === true && messages?.length == 0 ? (
              <Box
                display={"flex"}
                width={"100%"}
                justifyContent={"center"}
                alignItems={"center"}
                mt={"25vh"}
              >
                <CircularProgress size={20} />
              </Box>
            ) : loadingMessages === false && messages?.length == 0 ? (
              <>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    p: 4,
                    color: "text.secondary",
                  }}
                >
                  <Stack spacing={2} alignItems="center">
                    <IconMail sx={{ fontSize: 40, color: "grey.500" }} />
                    <Typography variant="h6">
                      Select a chat to get started
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose a conversation from the list to view messages.
                    </Typography>
                  </Stack>
                </Box>
              </>
            ) : (
              messages
                .slice()
                // .reverse()
                .map((msg, i) => {
                  const open = Boolean(popoverAnchorEl[msg.id]);
                  const id = open ? `popover-${msg.id}` : undefined;

                  return (
                    <Box
                      key={i}
                      display="flex"
                      justifyContent={
                        msg.role === "user" ? "flex-end" : "flex-start"
                      }
                      position="relative"
                    >
                      <Box
                        sx={{
                          my: d ? 0.3 : undefined,
                          bgcolor:
                            msg?.message_type === "image"
                              ? "transparent"
                              : msg.role === "user"
                              ? "primary.dark"
                              : "grey.300",
                          color:
                            msg.role === "user"
                              ? "primary.contrastText"
                              : "text.primary",
                          p: 1.5,
                          borderRadius: 2,
                          maxWidth: 330,
                          position: "relative",
                          "&:hover .info-btn": {
                            opacity: 1,
                          },
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: msg.role === "user" ? "right" : "left",
                            }}
                          >
                            {msg.role === "user" ? (
                              msg?.content
                            ) : (
                              <p
                                dangerouslySetInnerHTML={{
                                  __html: msg?.content,
                                }}
                              ></p>
                            )}
                          </Typography>
                          {d && (
                            <Typography
                              variant="body2"
                              sx={{
                                display: "block",
                                mt: 0.5,
                                fontSize: 10,
                                textAlign:
                                  msg.role === "user" ? "right" : "left",
                              }}
                            >
                              {moment(msg?.date).format("DD/MM/YYYY HH:mm")}
                            </Typography>
                          )}
                        </Box>

                        {/* Tooltip Icon shown only on hover */}
                      </Box>
                    </Box>
                  );
                })
            )}

            <div ref={bottomRef} />
          </Box>

          <Divider />

          {/* Input Area */}
        </Box>
      </Paper>
    </Box>
  );
}
